/**
 * AmountSummary.tsx
 * 金額集計テーブル。M0 では現状ラベルのまま。M1 で業務語に更新済み。
 *
 * M1 業務語ラベル対応済み:
 *   - 報酬額の合計（旧: 報酬額 小計 ①）
 *   - 登録税・印紙税（旧: 登録税等 小計 ②）
 *   - 消費税（10%）（旧: 消費税 ④）
 *   - 源泉所得税（旧: 源泉所得税 ⑤）
 *   - お振込いただく金額（旧: 差引受領額）
 * ①〜⑤ は title 属性（補助）として残す。
 */
import React from "react";
import { formatJPY } from "@juro/shared";

export interface AmountSummaryData {
  fee_subtotal: number;
  tax_subtotal: number;
  gross: number;
  consumption_tax: number;
  withholding_tax: number;
  total: number;
  adjustment_amount: number;
}

const inputStyle: React.CSSProperties = {
  padding: "5px 8px",
  border: "1px solid #cbd5e1",
  borderRadius: 5,
  fontSize: 13,
};

interface AmountSummaryProps {
  liveComputed: AmountSummaryData | null;
  /** 調整額（編集可能な場合は onAdjustmentChange も渡す） */
  adjustmentAmount: number;
  /** 渡した場合、調整額が編集可能な入力フィールドとして表示される */
  onAdjustmentChange?: (v: number) => void;
  /** 合計行ラベル（既定: 「お振込いただく金額」）。見積書表示時は呼び出し側から上書きする */
  totalLabel?: string;
}

export function AmountSummary({ liveComputed, adjustmentAmount, onAdjustmentChange, totalLabel = "お振込いただく金額" }: AmountSummaryProps) {
  const rows: { label: string; title?: string; key: keyof AmountSummaryData; negative?: boolean }[] = [
    { label: "報酬額の合計",   title: "報酬額 小計 ①", key: "fee_subtotal" },
    { label: "登録税・印紙税", title: "登録税等 小計 ②", key: "tax_subtotal" },
    { label: "消費税（10%）", title: "消費税 ④（①×10%）", key: "consumption_tax" },
    { label: "源泉所得税",     title: "源泉所得税 ⑤",  key: "withholding_tax", negative: true },
  ];

  return (
    <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
      <table style={{ borderCollapse: "collapse", fontSize: 13, minWidth: 340 }}>
        <tbody>
          {/* 調整額行 */}
          <tr style={{ borderBottom: "1px solid #f1f5f9" }}>
            <td style={{ padding: "6px 12px", color: "#475569" }}>調整額</td>
            <td style={{ padding: "6px 12px", textAlign: "right" }}>
              {onAdjustmentChange ? (
                <input
                  type="number"
                  value={adjustmentAmount}
                  onChange={(e) => onAdjustmentChange(Math.floor(Number(e.target.value)) || 0)}
                  style={{ ...inputStyle, width: 120, textAlign: "right" }}
                  placeholder="0"
                />
              ) : adjustmentAmount !== 0 ? (
                <span style={{ color: adjustmentAmount < 0 ? "#dc2626" : undefined }}>
                  {adjustmentAmount < 0 ? `- ${formatJPY(-adjustmentAmount)}` : formatJPY(adjustmentAmount)}
                </span>
              ) : null}
            </td>
          </tr>

          {rows.map(({ label, title, key, negative }) => {
            const value = liveComputed != null ? liveComputed[key] as number : null;
            return (
              <tr key={key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td
                  title={title}
                  style={{ padding: "5px 12px", color: "#94a3b8", fontSize: 12 }}
                >
                  {label}
                </td>
                <td
                  style={{
                    padding: "5px 12px",
                    textAlign: "right",
                    color: negative ? "#dc2626" : "#64748b",
                  }}
                >
                  {value != null
                    ? negative
                      ? `- ${formatJPY(value)}`
                      : formatJPY(value)
                    : "—"}
                </td>
              </tr>
            );
          })}

          {/* 合計行 */}
          <tr style={{ borderTop: "2px solid #0f766e" }}>
            <td style={{ padding: "8px 12px", fontWeight: 700, color: "#0f172a" }}>
              {totalLabel}
            </td>
            <td style={{ padding: "8px 12px", textAlign: "right", fontSize: 15, fontWeight: 800, color: "#0f766e" }}>
              {liveComputed != null ? formatJPY(liveComputed.total) : "—"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
